import type { StudentTestContent } from "p6m-p6u";
import type { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import { getIsLegacyTest } from "../../helpers/LegacyTest";
import { StudentTestAccessStates, getPersistedAnswersOwner } from "../../helpers/OnlineTestHelper";
import type { ClassMember } from "../../networking/schoolClasses";

/**
 * Two invariants:
 *
 * 1. The loading prefix is monotonic. Each phase carries what earlier phases proved, so there is no
 *    CHECKING_ACCESS without an identity.
 * 2. The verdict suffix is re-derivable, because access genuinely changes after READY: the pre-submit
 *    check can discover another tab's submission, and a mid-mount login changes who is asking.
 *
 * LOAD_FAILED sits outside both — nothing leaves it, since recovery is a page reload.
 */
export enum OnlineTestPhases {
    LOADING_TEST = "LOADING_TEST",
    LOADING_CLASS = "LOADING_CLASS",
    RESOLVING_IDENTITY = "RESOLVING_IDENTITY",
    CHECKING_ACCESS = "CHECKING_ACCESS",
    READY = "READY",
    LOGIN_REQUIRED = "LOGIN_REQUIRED",
    BLOCKED = "BLOCKED",
    ALREADY_SUBMITTED = "ALREADY_SUBMITTED",
    LOAD_FAILED = "LOAD_FAILED",
}

/** Decides whether the failure screen offers a retry: a missing test is a dead end, a dropped one is not. */
export enum LoadFailureKinds {
    /** 5xx, offline, timeout. */
    RECOVERABLE = "RECOVERABLE",
    /** 404/403 — retrying cannot help. */
    TERMINAL = "TERMINAL",
}

export enum OnlineTestActionTypes {
    TEST_LOADED = "TEST_LOADED",
    TEST_FAILED = "TEST_FAILED",
    CLASS_LOADED = "CLASS_LOADED",
    CLASS_FAILED = "CLASS_FAILED",
    OWNER_LOOKUP_REQUESTED = "OWNER_LOOKUP_REQUESTED",
    OWNER_LOOKUP_FAILED = "OWNER_LOOKUP_FAILED",
    IDENTITY_RESOLVED = "IDENTITY_RESOLVED",
    IDENTITY_CHANGED = "IDENTITY_CHANGED",
    ACCESS_RESOLVED = "ACCESS_RESOLVED",
}

// The fact bundles hold only what cannot be re-derived from them; everything derivable is a selector below.

export interface TestFacts {
    test: StudentTestContent;
    /**
     * `isLegacy` depends on this: a missing `shareMode` means "predates Enhanced Classes" on a real share
     * (`/student/:testId`) but "not shared yet" on a draft (`/create/preview`).
     */
    isSharedRecord: boolean;
    isLegacy: boolean;
}

export interface ClassFacts {
    /** null when there was no class to fetch: a legacy test, or a class-less share record. */
    visibility: SchoolClassVisibilities | null;
}

export interface IdentityFacts {
    isOwner: boolean;
    isPreview: boolean;
    /**
     * null means "nobody is logged in", never "still restoring" — IDENTITY_RESOLVED waits for the session.
     * Conflating the two reads as "not the member who already submitted", letting that member submit twice.
     */
    member: ClassMember | null;
}

export type OnlineTestState =
    | { phase: OnlineTestPhases.LOADING_TEST }
    | { phase: OnlineTestPhases.LOADING_CLASS; test: TestFacts }
    | {
          phase: OnlineTestPhases.LOAD_FAILED;
          kind: LoadFailureKinds;
          /** Absent when the test itself never loaded. */
          test?: TestFacts;
      }
    | {
          phase: OnlineTestPhases.RESOLVING_IDENTITY;
          test: TestFacts;
          schoolClass: ClassFacts;
          hasRequestedOwnerLookup: boolean;
      }
    | { phase: OnlineTestPhases.CHECKING_ACCESS; test: TestFacts; schoolClass: ClassFacts; identity: IdentityFacts }
    | { phase: OnlineTestPhases.READY; test: TestFacts; schoolClass: ClassFacts; identity: IdentityFacts }
    | {
          phase: OnlineTestPhases.LOGIN_REQUIRED;
          test: TestFacts;
          schoolClass: ClassFacts;
          identity: IdentityFacts;
          /** Non-optional so the login redirect needs no non-null assertion. */
          schoolClassId: string;
      }
    | { phase: OnlineTestPhases.BLOCKED; test: TestFacts; schoolClass: ClassFacts; identity: IdentityFacts }
    | { phase: OnlineTestPhases.ALREADY_SUBMITTED; test: TestFacts; schoolClass: ClassFacts; identity: IdentityFacts };

/** The phases that know who is asking, and so the ones ACCESS_RESOLVED and IDENTITY_CHANGED may act on. */
export type IdentifiedOnlineTestState = Extract<OnlineTestState, { identity: IdentityFacts }>;

export const hasResolvedIdentity = (state: OnlineTestState): state is IdentifiedOnlineTestState => "identity" in state;

/** What IDENTITY_RESOLVED and IDENTITY_CHANGED carry — identical to what the phase then stores. */
export type ResolvedIdentity = IdentityFacts;

export type OnlineTestActions =
    | { type: OnlineTestActionTypes.TEST_LOADED; test: StudentTestContent; isSharedRecord: boolean }
    | { type: OnlineTestActionTypes.TEST_FAILED; kind: LoadFailureKinds }
    | { type: OnlineTestActionTypes.CLASS_LOADED; visibility: SchoolClassVisibilities }
    | { type: OnlineTestActionTypes.CLASS_FAILED; kind: LoadFailureKinds }
    | { type: OnlineTestActionTypes.OWNER_LOOKUP_REQUESTED }
    | { type: OnlineTestActionTypes.OWNER_LOOKUP_FAILED }
    | { type: OnlineTestActionTypes.IDENTITY_RESOLVED; identity: ResolvedIdentity }
    | { type: OnlineTestActionTypes.IDENTITY_CHANGED; identity: ResolvedIdentity }
    | {
          type: OnlineTestActionTypes.ACCESS_RESOLVED;
          access: StudentTestAccessStates;
          needsPrivateLogin: boolean;
      };

export const initialOnlineTestState: OnlineTestState = { phase: OnlineTestPhases.LOADING_TEST };

const _buildTestFacts = (test: StudentTestContent, isSharedRecord: boolean): TestFacts => ({
    test,
    isSharedRecord,
    isLegacy: getIsLegacyTest(isSharedRecord ? test : null),
});

/** The backend calls it `groupId`. Normalised here because every comparison is case-insensitive. */
export const getSchoolClassId = (test: TestFacts): string | undefined =>
    test.test.groupId ? test.test.groupId.toUpperCase() : undefined;

/** Private classes gate on a member login; public ones do not. No class at all reads as public. */
export const getIsPrivateClass = (schoolClass: ClassFacts): boolean =>
    isPrivateSchoolClass(schoolClass.visibility ?? undefined);

/** Whether taking part needs no member session. A legacy test's null class resolves here. */
export const getIsLoginFreeActivity = (schoolClass: ClassFacts): boolean => !getIsPrivateClass(schoolClass);

/** A member of this test's own private class: the only identity that owns persisted answers. */
export const getIsPrivateAccess = (test: TestFacts, schoolClass: ClassFacts, member: ClassMember | null): boolean => {
    const schoolClassId = getSchoolClassId(test);
    const memberHasSameSchoolClassId = !!schoolClassId && member?.schoolClassId.toUpperCase() === schoolClassId;
    return getIsPrivateClass(schoolClass) && memberHasSameSchoolClassId;
};

export const getPersistedAnswersOwnerId = (
    test: TestFacts,
    schoolClass: ClassFacts,
    member: ClassMember | null
): string | null =>
    getPersistedAnswersOwner({
        isLoginFreeActivity: getIsLoginFreeActivity(schoolClass),
        isPrivateAccess: getIsPrivateAccess(test, schoolClass, member),
        classMemberId: member?.id ?? null,
    });

/**
 * Maps an access answer onto the phase that renders it. `needsPrivateLogin` outranks `access`: a visitor
 * who must log in is sent to the login screen before we care what their access would have been.
 */
const _toAccessPhase = (
    state: IdentifiedOnlineTestState,
    access: StudentTestAccessStates,
    needsPrivateLogin: boolean
): OnlineTestState => {
    const { test, schoolClass, identity } = state;

    if (needsPrivateLogin) {
        const schoolClassId = getSchoolClassId(test);

        // Unreachable today, but `needsPrivateLogin` arrives as an opaque boolean: fail closed rather
        // than letting a login-gated test through to READY on a contradiction.
        if (!schoolClassId) {
            return { phase: OnlineTestPhases.BLOCKED, test, schoolClass, identity };
        }

        return { phase: OnlineTestPhases.LOGIN_REQUIRED, test, schoolClass, identity, schoolClassId };
    }

    switch (access) {
        // Not a phase: the check has not answered yet, so hold position and keep the spinner up.
        case StudentTestAccessStates.PENDING:
            return state;
        case StudentTestAccessStates.ALREADY_SUBMITTED:
            return { phase: OnlineTestPhases.ALREADY_SUBMITTED, test, schoolClass, identity };
        case StudentTestAccessStates.BLOCKED:
            return { phase: OnlineTestPhases.BLOCKED, test, schoolClass, identity };
        case StudentTestAccessStates.ALLOWED:
            return { phase: OnlineTestPhases.READY, test, schoolClass, identity };
    }
};

export const onlineTestReducer = (state: OnlineTestState, action: OnlineTestActions): OnlineTestState => {
    switch (action.type) {
        case OnlineTestActionTypes.TEST_FAILED:
            if (state.phase !== OnlineTestPhases.LOADING_TEST) return state;
            return { phase: OnlineTestPhases.LOAD_FAILED, kind: action.kind };

        case OnlineTestActionTypes.TEST_LOADED: {
            if (state.phase !== OnlineTestPhases.LOADING_TEST) return state;

            const test = _buildTestFacts(action.test, action.isSharedRecord);

            // Legacy tests are class-less, so there is nothing to fetch and nothing to gate on. Keyed on
            // `isLegacy` rather than "has no class" to state the intent — and never reaching the class
            // fetch is what keeps a legacy test permanently login-free.
            if (test.isLegacy || !getSchoolClassId(test)) {
                return {
                    phase: OnlineTestPhases.RESOLVING_IDENTITY,
                    test,
                    schoolClass: { visibility: null },
                    hasRequestedOwnerLookup: false,
                };
            }

            return { phase: OnlineTestPhases.LOADING_CLASS, test };
        }

        case OnlineTestActionTypes.CLASS_LOADED:
            if (state.phase !== OnlineTestPhases.LOADING_CLASS) return state;
            return {
                phase: OnlineTestPhases.RESOLVING_IDENTITY,
                test: state.test,
                schoolClass: { visibility: action.visibility },
                hasRequestedOwnerLookup: false,
            };

        case OnlineTestActionTypes.CLASS_FAILED:
            if (state.phase !== OnlineTestPhases.LOADING_CLASS) return state;
            // Never falls through to "public": that would hand a private class's test to anyone whose
            // lookup failed. `test` survives so the error screen can still link back to the classroom.
            return {
                phase: OnlineTestPhases.LOAD_FAILED,
                kind: action.kind,
                test: state.test,
            };

        case OnlineTestActionTypes.OWNER_LOOKUP_REQUESTED:
            if (state.phase !== OnlineTestPhases.RESOLVING_IDENTITY) return state;
            if (state.hasRequestedOwnerLookup) return state;
            return { ...state, hasRequestedOwnerLookup: true };

        /*
         * Ownership must not be guessed: non-owner hands the test's own author the student flow, owner
         * asserts something unverified. Recoverable — the list fetch failed, the test is not gone.
         */
        case OnlineTestActionTypes.OWNER_LOOKUP_FAILED:
            if (state.phase !== OnlineTestPhases.RESOLVING_IDENTITY) return state;
            return {
                phase: OnlineTestPhases.LOAD_FAILED,
                kind: LoadFailureKinds.RECOVERABLE,
                test: state.test,
            };

        case OnlineTestActionTypes.IDENTITY_RESOLVED:
            // Answered once: a mid-flow login must not flip a student into owner preview. IDENTITY_CHANGED
            // is the only way identity moves afterwards, and it never touches ownership.
            if (state.phase !== OnlineTestPhases.RESOLVING_IDENTITY) return state;
            return {
                phase: OnlineTestPhases.CHECKING_ACCESS,
                test: state.test,
                schoolClass: state.schoolClass,
                identity: action.identity,
            };

        // A member logging in or out without a remount: ClassMemberContext.login() is a plain state
        // update, and so is the cross-tab storage listener.
        case OnlineTestActionTypes.IDENTITY_CHANGED: {
            if (!hasResolvedIdentity(state)) return state;

            // Only a change of person counts. A session rotation writes localStorage three times, so other
            // tabs get an event per write, each carrying a freshly parsed but equal member — rebuilding on
            // those would drop a mid-test student back to a spinner.
            if ((state.identity.member?.id ?? null) === (action.identity.member?.id ?? null)) return state;

            return {
                phase: OnlineTestPhases.CHECKING_ACCESS,
                test: state.test,
                schoolClass: state.schoolClass,
                // Ownership was answered in RESOLVING_IDENTITY and is not up for renegotiation: a
                // member logging in must not become the owner, nor strip a teacher of their preview.
                identity: { ...action.identity, isOwner: state.identity.isOwner },
            };
        }

        case OnlineTestActionTypes.ACCESS_RESOLVED:
            if (!hasResolvedIdentity(state)) return state;
            return _toAccessPhase(state, action.access, action.needsPrivateLogin);
    }
};

/** The phases that know which class this is: everything from RESOLVING_IDENTITY onward. */
type ClassifiedOnlineTestState = Extract<OnlineTestState, { schoolClass: ClassFacts }>;

const hasResolvedClass = (state: OnlineTestState): state is ClassifiedOnlineTestState => "schoolClass" in state;

/**
 * Keyed on identity, not on the class: RESOLVING_IDENTITY knows the class but is *by definition* still
 * waiting for the session. Answering "ready" there tells `useExistingTestResult` to look up a member that
 * has not arrived, which skips the submitted-check for the very member it was meant to find — a
 * resubmission bug.
 */
export const getIsClassMemberSessionReady = (state: OnlineTestState): boolean => hasResolvedIdentity(state);

export const getClassFacts = (state: OnlineTestState): ClassFacts | null =>
    hasResolvedClass(state) ? state.schoolClass : null;

/** What to do with a stored answer record, given who is asking. */
export enum PersistedAnswersDecisions {
    /** The record is this person's: load it. */
    RESTORE = "RESTORE",
    /**
     * Somebody's record, but nobody is logged in on a login-gated activity. Leave it untouched so its
     * owner gets their progress back when they log in — the decision is re-derived on IDENTITY_CHANGED.
     */
    HOLD = "HOLD",
    /**
     * Not this person's, and no login will recover it: a different member is here, or the activity is
     * login-free so there is no login to come back through. Progress must never be readable or
     * continuable across members.
     */
    DISCARD = "DISCARD",
    /** No identity yet — asking is premature, which is not the same as "no record". */
    PENDING = "PENDING",
}

/**
 * Whose answers these are is an access question, not a storage one — hence here rather than in
 * `useTestPersistence`, which consumes the verdict and keeps the mechanics.
 *
 * `storedOwner` is the record's `classMemberId` (historical key name), null when saved anonymously.
 */
export const getPersistedAnswersDecision = (
    state: OnlineTestState,
    storedOwner: string | null
): PersistedAnswersDecisions => {
    if (!hasResolvedIdentity(state)) return PersistedAnswersDecisions.PENDING;

    const currentOwner = getPersistedAnswersOwnerId(state.test, state.schoolClass, state.identity.member);
    if (storedOwner === currentOwner) return PersistedAnswersDecisions.RESTORE;

    const isLoginFree = getIsLoginFreeActivity(state.schoolClass);
    if (storedOwner !== null && currentOwner === null && !isLoginFree) return PersistedAnswersDecisions.HOLD;

    return PersistedAnswersDecisions.DISCARD;
};
